import { mayAutoRefresh, watchBrowserActivity } from "./nightActivity.mjs";
// Scoped snapshots are large: only the timer (or an explicit retry) may load
// them. Focus/visibility events must never bypass the failure budget.
export function watchScopedDataPolling(load, {
  onState = () => {},
  intervalMs = 5 * 60 * 1000,
  retryDelays = [10000, 30000, 60000, 120000, 300000],
  document: doc = document,
  setTimeout: setTimer = setTimeout,
  clearTimeout: clearTimer = clearTimeout,
  canRefresh = mayAutoRefresh, watchActivity = watchBrowserActivity,
} = {}) {
  let stopped = false;
  let pending = false;
  let failures = 0;
  let timer = null;
  let activeRequest = null;
  let lastSuccessAt = null;
  const publish = (status, error = "", nextDelayMs = null) => {
    if (!stopped) onState({ status, failures, error, nextDelayMs, lastSuccessAt });
  };
  const schedule = delay => {
    clearTimer(timer);
    if (!stopped) timer = setTimer(run, delay);
  };
  async function run() {
    clearTimer(timer);
    timer = null;
    if (stopped || pending) return;
    if (doc.visibilityState === "hidden" || !canRefresh()) {
      schedule(intervalMs);
      return;
    }
    pending = true;
    activeRequest = new AbortController();
    publish("loading");
    try {
      await load(activeRequest.signal);
      if (stopped) return;
      failures = 0;
      lastSuccessAt = Date.now();
      publish("ready");
      schedule(intervalMs);
    } catch (error) {
      if (stopped) return;
      failures += 1;
      const message = error?.message || "Falha ao carregar os dados do CRM";
      const delay = retryDelays[Math.min(failures - 1,retryDelays.length - 1)] || intervalMs;
      publish("retrying", message, delay);
      schedule(delay);
    } finally {
      pending = false;
      activeRequest = null;
    }
  }
  const unwatchActivity = watchActivity(() => {
    // Activity is not an automatic retry of a failed request. Keep the existing
    // failure/backoff budget; only wake an idle, healthy polling loop.
    if (canRefresh() && !pending && failures === 0) void run();
  });
  void run();
  return {
    retry() {
      // Repeated clicks and rerenders cannot start another in-flight request.
      if (stopped || pending || failures === 0) return;
      clearTimer(timer);
      void run();
    },
    stop() {
      stopped = true;
      unwatchActivity();
      clearTimer(timer);
      activeRequest?.abort();
    },
  };
}
